/**
 * Requirement Compliance Service
 * Handles fetching compliance data for the Compliance Management Dashboard
 * API: GET /api/RequirementCompliance/{entityId}
 */

import { env } from '@/config/env';
import { vehicleService } from '@/services/vehicleService';
import { VehicleDetailDto } from '@/types/manage';
// Re-export types from complianceRequirementsView for consistency
import {
  ComplianceRequirementsViewResponse,
  EntitySummary,
  Scope,
  Role,
  RequirementWithStatus,
  RequirementDetail,
  AppliesTo,
  RequirementType,
  ExpirationInfo,
  RequirementStatus,
  ScopeInfo,
  ComplianceStatus,
  ComplianceRequirementsDataAdapter,
  ScopeRoleComplianceCount,
  RequirementGroup,
  VehicleAssessmentSummary,
} from '@/types/complianceRequirementsView';
import { mockComplianceRequirementsViewData } from '@/data/mockComplianceRequirementsViewData';

// Re-export types for consumers
export type {
  ComplianceRequirementsViewResponse as RequirementComplianceResponse,
  EntitySummary,
  Scope,
  Role,
  RequirementWithStatus,
  RequirementDetail as Requirement,
  AppliesTo,
  RequirementType,
  ExpirationInfo as Expiration,
  RequirementStatus,
  ScopeInfo as RequirementScope,
  ScopeRoleComplianceCount,
  RequirementGroup,
};

export type ComplianceStatusType = ComplianceStatus;
export type ScopeType = 'EverDriven' | 'State' | 'District' | 'Vehicle' | 'Shared';
export { ComplianceRequirementsDataAdapter };

// Pagination options for the API
export interface PaginationOptions {
  pageNumber?: number;
  pageSize?: number;
  enablePagination?: boolean;
}

// Use centralized mock data from mockComplianceRequirementsViewData
const mockRequirementComplianceResponse = mockComplianceRequirementsViewData;

// ============================================================================
// API Response Transformation
// ============================================================================

/**
 * Transforms a single requirement to normalize notificationFrequency
 */
function transformRequirement(reqWithStatus: RequirementWithStatus): RequirementWithStatus {
  const req = reqWithStatus.requirement;

  // Normalize notificationFrequency to string for UI compatibility
  let notificationFrequencyStr = '';
  if (req.notificationFrequency) {
    if (typeof req.notificationFrequency === 'string') {
      notificationFrequencyStr = req.notificationFrequency;
    } else if (typeof req.notificationFrequency === 'object') {
      notificationFrequencyStr = req.notificationFrequency.notificationFrequencyName || '';
    }
  }

  return {
    ...reqWithStatus,
    requirement: {
      ...req,
      notificationFrequency: notificationFrequencyStr,
    },
  };
}

/**
 * Transforms the raw API response to ensure all expected fields are present
 * - Derives scopeId and scopeName from requirements when not present at scope level
 * - Normalizes notificationFrequency to string for UI compatibility
 * - Flattens requirementGroups into requirements array for backward compatibility
 */
function transformApiResponse(
  apiResponse: ComplianceRequirementsViewResponse
): ComplianceRequirementsViewResponse {
  const transformedScopes = apiResponse.scopes?.map((scope, scopeIndex) => {
    // Try to derive scopeId and scopeName from the first requirement's scope info
    let derivedScopeId = scope.scopeId;
    let derivedScopeName = scope.scopeName;

    // Transform roles and their requirements
    const transformedRoles = scope.roles?.map((role) => {
      // Collect requirements from both direct requirements and requirementGroups
      let allRequirements: RequirementWithStatus[] = [];

      // Add direct requirements (if any)
      if (role.requirements && role.requirements.length > 0) {
        allRequirements = [...role.requirements];
      }

      // Flatten requirementGroups into requirements array (for State/District scopes)
      if (role.requirementGroups && role.requirementGroups.length > 0) {
        for (const group of role.requirementGroups) {
          if (group.requirements && group.requirements.length > 0) {
            allRequirements = [...allRequirements, ...group.requirements];
          }
        }
      }

      // Transform all requirements
      const transformedRequirements = allRequirements.map(transformRequirement);

      // Try to derive scope info from first requirement
      if ((!derivedScopeId || !derivedScopeName) && transformedRequirements.length > 0) {
        const firstReq = transformedRequirements[0];
        if (firstReq?.requirement?.scope) {
          derivedScopeId = derivedScopeId || firstReq.requirement.scope.scopeId;
          derivedScopeName = derivedScopeName || firstReq.requirement.scope.scopeName;
        }
      }

      // Build the transformed role object
      const transformedRole: Role = {
        ...role,
        requirements: transformedRequirements,
      };

      // Keep requirementGroups for components that need grouped view (only if present)
      if (role.requirementGroups && role.requirementGroups.length > 0) {
        transformedRole.requirementGroups = role.requirementGroups.map(group => ({
          ...group,
          requirements: group.requirements?.map(transformRequirement) || [],
        }));
      }

      return transformedRole;
    }) || [];

    // If still no scopeId/scopeName, generate from scopeType
    if (!derivedScopeId) {
      derivedScopeId = `${scope.scopeType}-${scopeIndex}`;
    }
    if (!derivedScopeName) {
      derivedScopeName = `${scope.scopeType} Requirements`;
    }

    return {
      ...scope,
      scopeId: derivedScopeId,
      scopeName: derivedScopeName,
      roles: transformedRoles,
    };
  }) || [];

  return {
    entitySummary: apiResponse.entitySummary || { status: 'Unknown', score: 0 },
    scopes: transformedScopes,
    ...(apiResponse.vehicleAssessments && {
      vehicleAssessments: transformVehicleRequirements(apiResponse.vehicleAssessments),
    }),
  };
}

const MAX_CONCURRENT_VEHICLE_FETCHES = 5;

async function runWithConcurrencyLimit<T>(
  fns: Array<() => Promise<T>>,
  limit: number,
): Promise<T[]> {
  const results = new Array<T>(fns.length);
  let next = 0;
  const worker = async () => {
    while (next < fns.length) {
      const i = next++;
      const fn = fns[i];
      if (fn) results[i] = await fn();
    }
  };
  await Promise.all(Array.from({ length: Math.min(limit, fns.length) }, worker));
  return results;
}

/**
 * Enriches assessment vehicle summaries with profile fields from vehicle-service.
 * This keeps assessment as source of compliance results while vehicle-service
 * remains source of truth for vehicle profile metadata.
 */
async function enrichVehicleAssessments(
  data: ComplianceRequirementsViewResponse,
  _entityId: string
): Promise<ComplianceRequirementsViewResponse> {
  if (!data.vehicleAssessments || data.vehicleAssessments.length === 0) {
    return data;
  }

  try {
    const assessments = data.vehicleAssessments;
    const detailFns = assessments.map((vehicle) => async () => {
      try {
        return await vehicleService.getVehicleById(vehicle.vehicleId);
      } catch (error) {
        if (import.meta.env.DEV) {
          console.warn(`Failed to fetch details for vehicle ${vehicle.vehicleId}:`, error);
        }
        return null;
      }
    });

    const vehicleDetailsArray = await runWithConcurrencyLimit(detailFns, MAX_CONCURRENT_VEHICLE_FETCHES);

    const detailsById = new Map<string, VehicleDetailDto>();
    vehicleDetailsArray.forEach((detail, index) => {
      if (detail) {
        detailsById.set(assessments[index]!.vehicleId, detail);
      }
    });

    return {
      ...data,
      vehicleAssessments: data.vehicleAssessments.map(vehicle => {
        const detail = detailsById.get(vehicle.vehicleId);
        if (!detail) {
          return vehicle;
        }

        return {
          ...vehicle,
          operationalStatus: detail.statusName,
          isPrimary: detail.isPrimary,
          make: detail.make,
          model: detail.model,
          year: detail.year,
          color: detail.colorName || detail.color,
          vehicleType: detail.typeName || detail.kindName,
          wheelchairAccessible: detail.wheelChairTypeId != null,
          capacity: detail.seatingCapacity,
        };
      }),
    };
  } catch (error) {
    // Non-blocking enrichment failure: keep compliance response functional.
    if (import.meta.env.DEV) {
      console.warn('Vehicle enrichment failed, continuing with assessment data only:', error);
    }
    return data;
  }
}

// ============================================================================
// Service Implementation
// ============================================================================

function resolvePaginationOptions(options?: PaginationOptions): PaginationOptions {
  return {
    pageNumber: options?.pageNumber ?? 1,
    pageSize: options?.pageSize ?? 50,
    enablePagination: options?.enablePagination ?? true,
  };
}

/**
 * Picks between the compute endpoint (recalculates on every call) and the persisted endpoint
 * (serves the stored snapshot). Both return the same ComplianceRequirementsViewResponse shape, so
 * nothing downstream of these functions needs to know which one was used.
 *
 * @see env.usePersistedCompliance
 */
function complianceRoute(entityId: string): string {
  return env.usePersistedCompliance
    ? `RequirementCompliance/persisted/${entityId}`
    : `RequirementCompliance/${entityId}`;
}

function serviceProviderComplianceRoute(serviceProviderId: string): string {
  return env.usePersistedCompliance
    ? `RequirementCompliance/persisted/service-provider/${serviceProviderId}`
    : `RequirementCompliance/service-provider/${serviceProviderId}`;
}

async function fetchComplianceFromUrl(
  baseUrl: string,
  entityId: string,
  paginationOptions: PaginationOptions,
  errorContext: string
): Promise<ComplianceRequirementsViewResponse> {
  const queryParams = new URLSearchParams({
    pageNumber: paginationOptions.pageNumber!.toString(),
    pageSize: paginationOptions.pageSize!.toString(),
    enablePagination: paginationOptions.enablePagination!.toString(),
  });
  const url = `${baseUrl}?${queryParams.toString()}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Accept': 'application/json',
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Failed to fetch ${errorContext}: ${response.status} - ${errorText}`);
  }

  const rawData = await response.json();
  const transformedData = transformApiResponse(rawData);
  return enrichVehicleAssessments(transformedData, entityId);
}

/**
 * Fetches requirement compliance data for an entity
 * Falls back to mock data when API is unavailable or useMockData is enabled
 *
 * @param entityId - The entity ID to fetch compliance data for (REQUIRED)
 * @param options - Pagination options (pageNumber, pageSize, enablePagination)
 */
export async function fetchRequirementCompliance(
  entityId: string,
  options?: PaginationOptions
): Promise<ComplianceRequirementsViewResponse> {
  if (!entityId) {
    throw new Error('entityId is required for fetchRequirementCompliance');
  }

  const paginationOptions = resolvePaginationOptions(options);

  if (env.useMockData) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return { ...mockRequirementComplianceResponse };
  }

  try {
    const baseUrl = `${env.assessmentServiceBaseUrl}/${complianceRoute(entityId)}`;
    return await fetchComplianceFromUrl(baseUrl, entityId, paginationOptions, 'requirement compliance');
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error fetching requirement compliance:', error);
    }
    throw error;
  }
}

/**
 * Fetches requirement compliance data for a service provider. The BE snapshots
 * SP-level requirements + fleet vehicle assessments under OwnerType=ServiceProvider,
 * shaped as the same ComplianceRequirementsViewResponse the user endpoint returns.
 *
 * @param serviceProviderId - The service provider ID (REQUIRED)
 * @param options - Pagination options (pageNumber, pageSize, enablePagination)
 */
export async function fetchServiceProviderCompliance(
  serviceProviderId: string,
  options?: PaginationOptions
): Promise<ComplianceRequirementsViewResponse> {
  if (!serviceProviderId) {
    throw new Error('serviceProviderId is required for fetchServiceProviderCompliance');
  }

  const paginationOptions = resolvePaginationOptions(options);

  if (env.useMockData) {
    await new Promise((resolve) => setTimeout(resolve, 300));
    return { ...mockRequirementComplianceResponse };
  }

  try {
    const baseUrl = `${env.assessmentServiceBaseUrl}/${serviceProviderComplianceRoute(serviceProviderId)}`;
    return await fetchComplianceFromUrl(baseUrl, serviceProviderId, paginationOptions, 'service provider compliance');
  } catch (error) {
    if (import.meta.env.DEV) {
      console.error('Error fetching service provider compliance:', error);
    }
    throw error;
  }
}

interface RawVehicleRequirement {
  complianceStatus?: string;
  evidenceStatus?: string;
  approvedCompletionDate?: string | null;
  requirementExpirationDate?: string | null;
  requirement?: {
    id?: string;
    name?: string;
    expectedCompletionDate?: string | null;
    status?: { statusName?: string };
    requirementType?: { typeName?: string };
    appliesTo?: Array<{ appliesToName?: string }>;
    exceptionsAllowed?: boolean;
    impacting?: boolean;
    completionAssistingRole?: { completionAssistingRoleId: string; completionAssistingRoleName: string } | null;
  };
}

interface RawVehicleAssessment extends Omit<VehicleAssessmentSummary, 'requirements'> {
  requirements?: RawVehicleRequirement[];
}

/**
 * Transform vehicle requirement assessment results to include requirement details
 * extracted from the Requirement object (Type, Role, etc.)
 */
function transformVehicleRequirements(
  vehicleAssessments: RawVehicleAssessment[]
): VehicleAssessmentSummary[] {
  if (!vehicleAssessments) {
    return [];
  }

  return vehicleAssessments.map(vehicle => ({
    ...vehicle,
    requirements: (vehicle.requirements || []).map((assessment) => ({
      expectedCompletionDate: assessment.requirement?.expectedCompletionDate || null,
      requirementId: assessment.requirement?.id || '',
      requirementName: assessment.requirement?.name || '',
      status: assessment.requirement?.status?.statusName || '',
      complianceStatus: assessment.complianceStatus || '',
      evidenceStatus: assessment.evidenceStatus || '',
      lastValidationActionAt: assessment.approvedCompletionDate || null,
      approvedCompletionDate: assessment.approvedCompletionDate || null,
      requirementExpirationDate: assessment.requirementExpirationDate || null,
      // Extract from requirement object
      requirementType: assessment.requirement?.requirementType?.typeName || '',
      role: assessment.requirement?.appliesTo?.[0]?.appliesToName || '',
      completionDate: assessment.approvedCompletionDate || null,
      exceptionsAllowed: assessment.requirement?.exceptionsAllowed ?? false,
      impacting: assessment.requirement?.impacting ?? false,
      completionAssistingRole: assessment.requirement?.completionAssistingRole ?? null,
    })),
  }));
}

export { mockRequirementComplianceResponse };
